<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();

const showAddForm = ref(false);
const editingStage = ref(null);
const newStageName = ref('');
const newStageColor = ref('#1F93FF');
const editName = ref('');
const editColor = ref('');

const stages = computed(() => store.getters['kanbanStages/getStages']);
const uiFlags = computed(() => store.getters['kanbanStages/getUIFlags']);

onBeforeMount(() => {
  store.dispatch('kanbanStages/get');
});

const colors = [
  '#1F93FF', '#44CE4B', '#FFB800', '#FF4C51',
  '#7C3AED', '#EC4899', '#14B8A6', '#F97316',
];

async function addStage() {
  if (!newStageName.value.trim()) return;
  try {
    await store.dispatch('kanbanStages/create', {
      name: newStageName.value.trim(),
      color: newStageColor.value,
    });
    newStageName.value = '';
    newStageColor.value = '#1F93FF';
    showAddForm.value = false;
    useAlert('Estágio criado com sucesso!');
  } catch (err) {
    useAlert('Erro ao criar estágio');
  }
}

function startEdit(stage) {
  editingStage.value = stage.id;
  editName.value = stage.name;
  editColor.value = stage.color;
}

function cancelEdit() {
  editingStage.value = null;
}

async function saveEdit(id) {
  if (!editName.value.trim()) return;
  try {
    await store.dispatch('kanbanStages/update', {
      id,
      name: editName.value.trim(),
      color: editColor.value,
    });
    editingStage.value = null;
    useAlert('Estágio atualizado!');
  } catch (err) {
    useAlert('Erro ao atualizar estágio');
  }
}

async function deleteStage(id) {
  if (!confirm('Tem certeza que deseja deletar este estágio?')) return;
  try {
    await store.dispatch('kanbanStages/delete', id);
    useAlert('Estágio deletado!');
  } catch (err) {
    useAlert('Erro ao deletar estágio');
  }
}
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    loading-message="Carregando estágios..."
    :no-records-found="!stages.length && !showAddForm"
    no-records-message="Nenhum estágio Kanban criado. Crie o primeiro!"
  >
    <template #header>
      <BaseSettingsHeader
        title="Estágios Kanban"
        description="Gerencie os estágios do quadro Kanban para organizar suas conversas."
      >
        <template #actions>
          <Button
            label="Novo Estágio"
            size="sm"
            @click="showAddForm = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <!-- Add Form -->
      <div
        v-if="showAddForm"
        class="mb-4 flex items-center gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-4"
      >
        <input
          v-model="newStageName"
          type="text"
          placeholder="Nome do estágio"
          class="flex-1 rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-woot-500"
          @keyup.enter="addStage"
        />
        <div class="flex gap-1">
          <button
            v-for="c in colors"
            :key="c"
            class="h-6 w-6 rounded-full border-2 transition-transform hover:scale-110"
            :class="newStageColor === c ? 'border-n-slate-12 scale-110' : 'border-transparent'"
            :style="{ backgroundColor: c }"
            @click="newStageColor = c"
          />
        </div>
        <Button label="Salvar" size="sm" @click="addStage" />
        <Button
          label="Cancelar"
          size="sm"
          variant="hollow"
          @click="showAddForm = false"
        />
      </div>

      <!-- Stages List -->
      <div class="flex flex-col gap-2">
        <div
          v-for="stage in stages"
          :key="stage.id"
          class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-4 transition-colors hover:bg-n-alpha-1"
        >
          <div
            class="h-4 w-4 flex-shrink-0 rounded-full"
            :style="{ backgroundColor: stage.color }"
          />

          <template v-if="editingStage === stage.id">
            <input
              v-model="editName"
              type="text"
              class="flex-1 rounded-md border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-woot-500"
              @keyup.enter="saveEdit(stage.id)"
            />
            <div class="flex gap-1">
              <button
                v-for="c in colors"
                :key="c"
                class="h-5 w-5 rounded-full border-2 transition-transform hover:scale-110"
                :class="editColor === c ? 'border-n-slate-12 scale-110' : 'border-transparent'"
                :style="{ backgroundColor: c }"
                @click="editColor = c"
              />
            </div>
            <Button label="Salvar" size="xs" @click="saveEdit(stage.id)" />
            <Button
              label="Cancelar"
              size="xs"
              variant="hollow"
              @click="cancelEdit"
            />
          </template>

          <template v-else>
            <span class="flex-1 text-sm font-medium text-n-slate-12">
              {{ stage.name }}
            </span>
            <span class="text-xs text-n-slate-10">Posição: {{ stage.position }}</span>
            <Button
              label="Editar"
              size="xs"
              variant="hollow"
              @click="startEdit(stage)"
            />
            <Button
              label="Deletar"
              size="xs"
              variant="hollow"
              color-scheme="alert"
              @click="deleteStage(stage.id)"
            />
          </template>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
